import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import MagicOnboardingPageV2 from '@/pages/MagicOnboardingPageV2';
import { V2C, fontBody } from '@/components/onboarding/v2/shared/tokens';
import { V2Btn, V2GlobalStyles, V2InlineMsg, V2Input, V2Label } from '@/components/onboarding/v2/shared/primitives';
import V2Step from '@/components/onboarding/v2/shared/V2Step';
import V2ThemePicker from '@/components/onboarding/v2/shared/V2ThemePicker';
import PreviewBookingFlow, { type PreviewBookingFlowHandle } from '@/components/draft-preview/PreviewBookingFlow';
import { FlowLinkInBio } from '@/components/booking-flow/FlowLinkInBio';
import { getBookingTheme } from '@/components/booking-flow/themes';
import { DEFAULT_BOOKING_THEME_KEY, loadV2Draft, saveV2Draft } from '@/utils/v2OnboardingDraft';
import { buildPreviewDraft, isPreviewDraft, PREVIEW_SERVICE_DURATION, PREVIEW_SERVICE_PRICE } from '@/utils/previewFirstDraft';
import { posthog, trackEvent } from '@/lib/posthog';

const SHELL_BACKGROUND = 'radial-gradient(ellipse at 50% -10%, #E3E3FD 0%, #F8F8FF 55%)';
const DEMO_SCALE = 0.6;
const DEMO_SOURCE_WIDTH = 360;
const DEMO_SOURCE_HEIGHT = 610;

type PreviewStage = 'empty' | 'personalized' | 'ready';

function ReplayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M19.6 8.2A8.25 8.25 0 1 0 20.25 15" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
      <path d="M19.55 3.95v4.8h-4.8" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PreviewBuilder({ onComplete }: { onComplete: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialDraft = useMemo(() => loadV2Draft(), []);
  const [businessName, setBusinessName] = useState(initialDraft?.businessName ?? '');
  const [serviceName, setServiceName] = useState(initialDraft?.services?.[0]?.name ?? '');
  const [themeKey, setThemeKey] = useState(initialDraft?.bookingFlowThemeKey || DEFAULT_BOOKING_THEME_KEY);
  const [storageError, setStorageError] = useState('');
  const [simulationComplete, setSimulationComplete] = useState(false);
  const previewFlowRef = useRef<PreviewBookingFlowHandle>(null);
  const personalizedTracked = useRef(false);
  const readyTracked = useRef(false);
  const publishImpressionTracked = useRef(false);
  const changedFieldsTracked = useRef<Set<string>>(new Set());
  const startedAt = useRef(Date.now());
  const latestStateRef = useRef({ stage: 'empty' as PreviewStage, themeKey, publishImpressionShown: false });

  useEffect(() => {
    try { posthog.startSessionRecording?.(); } catch { /* analytics must not block UI */ }
    trackEvent('preview_started', {
      entry_path: location.pathname,
      has_existing_draft: Boolean(initialDraft),
      entry_source: searchParams.get('source') ?? 'landing_cta',
    });
    const handlePageHide = () => {
      const latest = latestStateRef.current;
      trackEvent('preview_exited', {
        preview_stage: latest.stage,
        theme_key: latest.themeKey,
        publish_impression_shown: latest.publishImpressionShown,
        total_elapsed_seconds: Math.round((Date.now() - startedAt.current) / 1000),
      });
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const personalized = Boolean(businessName.trim());
  const ready = personalized && Boolean(serviceName.trim());
  const previewStage: PreviewStage = ready ? 'ready' : personalized ? 'personalized' : 'empty';
  const theme = useMemo(() => getBookingTheme(themeKey), [themeKey]);
  const previewData = useMemo(() => buildPreviewDraft(initialDraft, { businessName, serviceName, themeKey }), [initialDraft, businessName, serviceName, themeKey]);

  latestStateRef.current = { stage: previewStage, themeKey, publishImpressionShown: publishImpressionTracked.current };

  useEffect(() => { setSimulationComplete(false); }, [businessName, serviceName, themeKey]);

  useEffect(() => {
    if (!personalized || personalizedTracked.current) return;
    personalizedTracked.current = true;
    trackEvent('preview_personalized', { has_business_name: true, time_to_personalize_seconds: Math.round((Date.now() - startedAt.current) / 1000) });
  }, [personalized]);

  useEffect(() => {
    if (!ready || readyTracked.current) return;
    readyTracked.current = true;
    trackEvent('preview_ready', { theme_key: themeKey, has_service: true, time_to_ready_seconds: Math.round((Date.now() - startedAt.current) / 1000) });
  }, [ready, themeKey]);

  useEffect(() => {
    if (!simulationComplete || publishImpressionTracked.current) return;
    publishImpressionTracked.current = true;
    latestStateRef.current.publishImpressionShown = true;
    trackEvent('preview_booking_simulated', { theme_key: themeKey, service_name_entered: true, elapsed_seconds: Math.round((Date.now() - startedAt.current) / 1000) });
    trackEvent('preview_publish_impression', { theme_key: themeKey, time_to_impression_seconds: Math.round((Date.now() - startedAt.current) / 1000) });
  }, [simulationComplete, themeKey]);

  function trackFirstFieldChange(fieldName: string) {
    if (changedFieldsTracked.current.has(fieldName)) return;
    changedFieldsTracked.current.add(fieldName);
    trackEvent('preview_field_changed', { field_name: fieldName, time_to_first_change_seconds: Math.round((Date.now() - startedAt.current) / 1000) });
  }

  const handleSimulationComplete = useCallback(() => setSimulationComplete(true), []);

  function savePreviewAndContinue() {
    if (!ready) return;
    const existing = loadV2Draft();
    const draft = buildPreviewDraft(existing, { businessName, serviceName, themeKey });
    try {
      saveV2Draft(draft);
    } catch {
      setStorageError('We could not save your preview on this device. Please check your browser storage settings and try again.');
      trackEvent('preview_publish_failed', { failure_reason: 'draft_storage', theme_key: themeKey });
      return;
    }
    trackEvent('preview_publish_clicked', {
      has_existing_draft: Boolean(existing),
      theme_key: themeKey,
      time_to_publish_intent_seconds: Math.round((Date.now() - startedAt.current) / 1000),
      auth_state: 'anonymous',
      simulation_complete: simulationComplete,
    });
    onComplete();
    navigate('/magic-onboarding-v2?step=1&preview_saved=1', { replace: true });
  }

  const previewServices = useMemo(() => serviceName.trim() ? [{
    id: `preview-${serviceName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    name: serviceName.trim(), description: '', price: PREVIEW_SERVICE_PRICE, duration: PREVIEW_SERVICE_DURATION,
  }] : [], [serviceName]);

  const ctaBackground = ready
    ? 'radial-gradient(120% 180% at 18% -55%, rgba(255,255,255,.56), transparent 48%), linear-gradient(135deg, rgba(112,108,255,.98), rgba(75,70,238,.94))'
    : 'radial-gradient(120% 180% at 18% -55%, rgba(255,255,255,.42), transparent 48%), linear-gradient(135deg, rgba(154,151,246,.58), rgba(116,111,225,.48))';

  return (
    <div style={{ minHeight: '100dvh', width: '100%', maxWidth: '100vw', overflowX: 'hidden', background: SHELL_BACKGROUND, display: 'flex', flexDirection: 'column', margin: '0 auto', position: 'relative', fontFamily: fontBody, boxSizing: 'border-box' }}>
      <V2GlobalStyles />
      <style>{`
        html, body, #root { max-width: 100%; overflow-x: hidden; }
        *, *::before, *::after { box-sizing: border-box; }
        .preview-cta-shell { transition: background .35s ease, border-color .35s ease, box-shadow .35s ease; }
        .preview-success-copy { transition: max-height .42s cubic-bezier(.2,.8,.2,1), opacity .28s ease, transform .36s cubic-bezier(.2,.8,.2,1), margin .36s ease; }
        @media (prefers-reduced-motion: reduce) { .preview-cta-shell, .preview-success-copy { transition: none; } }
      `}</style>

      <div style={{ width: '100%', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 0', gap: 10, minWidth: 0 }}>
          <Link to="/" className="flex items-center gap-2 no-underline" style={{ minWidth: 0 }}>
            <img src="/uploads/figgy_icon_transparent.png" alt="" width={30} height={30} style={{ width: 30, height: 30, objectFit: 'contain', flexShrink: 0 }} />
            <span className="font-display font-bold text-[1.55rem] tracking-tight text-figgy-500" style={{ lineHeight: 1 }}>Figgy</span>
          </Link>
          <span style={{ fontSize: 11, fontWeight: 600, color: V2C.brand, background: V2C.brandSofter, border: `1px solid ${V2C.line}`, padding: '4px 9px', borderRadius: 99, whiteSpace: 'nowrap', flexShrink: 0 }}>No account required</span>
        </div>

        <V2Step stepIdx={0} totalSteps={2} title="Build your booking page" subtitle="Add one service and choose your look. Then watch the exact Figgy booking flow play through.">
          <div style={{ marginBottom: 16, width: '100%', minWidth: 0 }}>
            <V2Label>Watch a customer book you—before you even sign up</V2Label>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
              <div aria-label="Animated customer booking experience" style={{ position: 'relative', width: DEMO_SOURCE_WIDTH * DEMO_SCALE, height: DEMO_SOURCE_HEIGHT * DEMO_SCALE, borderRadius: 18, overflow: 'hidden', boxShadow: `0 12px 28px rgba(39,35,104,.14), inset 0 0 0 1px ${V2C.line}`, background: theme.flowBg || '#fff', flexShrink: 0 }}>
                {ready && <button type="button" aria-label="Replay booking demo" onClick={() => { setSimulationComplete(false); previewFlowRef.current?.restart(); trackEvent('preview_booking_restarted', { theme_key: themeKey }); }} style={{ position: 'absolute', top: 7, right: 7, zIndex: 20, width: 30, height: 30, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(60,60,67,.14)', background: 'rgba(248,248,248,.94)', color: '#8E8E93', boxShadow: '0 2px 8px rgba(0,0,0,.08)', cursor: 'pointer', WebkitTapHighlightColor: 'transparent', WebkitBackdropFilter: 'blur(14px)', backdropFilter: 'blur(14px)' }}><ReplayIcon /></button>}
                <div style={{ width: DEMO_SOURCE_WIDTH, height: DEMO_SOURCE_HEIGHT, transform: `scale(${DEMO_SCALE})`, transformOrigin: 'top left', pointerEvents: 'none' }}>
                  {ready ? <PreviewBookingFlow ref={previewFlowRef} key={`${businessName}-${serviceName}-${themeKey}`} theme={theme} businessName={previewData.businessName || businessName} providerName={previewData.businessName || businessName} services={previewServices} onBack={() => undefined} autoPlay onComplete={handleSimulationComplete} /> : <FlowLinkInBio theme={theme} businessName="Your Business" displayName="Your Business" bio="Simple online booking, made for you." onBook={() => undefined} showBookButton bookButtonLabel="Book appointment" />}
                </div>
              </div>
            </div>
          </div>

          <V2Input label="Business name" value={businessName} onChange={(value) => { trackFirstFieldChange('business_name'); setBusinessName(value); }} placeholder="Example: Maya Cuts" autoComplete="organization" onboardingField="preview_business_name" />
          <V2Input label="Your first service" value={serviceName} onChange={(value) => { trackFirstFieldChange('service_name'); setServiceName(value); }} placeholder="Example: Signature haircut" hint={`We'll start it at ${PREVIEW_SERVICE_DURATION} minutes and $${PREVIEW_SERVICE_PRICE}. You can edit this later.`} onboardingField="preview_service_name" />
          <V2ThemePicker value={themeKey} onChange={(nextThemeKey) => { if (nextThemeKey === themeKey) return; trackEvent('preview_theme_selected', { from_theme_key: themeKey, to_theme_key: nextThemeKey, preview_stage: previewStage }); setThemeKey(nextThemeKey); }} onboardingField="preview_theme" />
          {storageError && <div role="alert"><V2InlineMsg kind="error">{storageError}</V2InlineMsg></div>}

          <div className="preview-cta-shell" style={{ position: 'sticky', bottom: 8, zIndex: 5, marginTop: 16, padding: 14, borderRadius: 20, background: 'linear-gradient(145deg, rgba(255,255,255,.72), rgba(239,238,255,.68))', border: '1px solid rgba(255,255,255,.76)', boxShadow: '0 14px 36px rgba(34,30,94,.14), inset 0 1px 0 rgba(255,255,255,.82)', WebkitBackdropFilter: 'blur(20px) saturate(150%)', backdropFilter: 'blur(20px) saturate(150%)', width: '100%', minWidth: 0 }}>
            <div className="preview-success-copy" aria-hidden={!simulationComplete} style={{ maxHeight: simulationComplete ? 110 : 0, opacity: simulationComplete ? 1 : 0, transform: simulationComplete ? 'translateY(0)' : 'translateY(8px)', overflow: 'hidden', marginBottom: simulationComplete ? 12 : 0, textAlign: 'center' }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: V2C.brand }}>Your page works.</div>
              <div style={{ fontSize: 19, lineHeight: 1.2, fontWeight: 900, color: V2C.ink, marginTop: 5 }}>Now make the next booking real.</div>
              <div style={{ fontSize: 12.5, color: V2C.mute, marginTop: 6, lineHeight: 1.45 }}>Save your page, finish setup, and start accepting appointments.</div>
            </div>
            <V2Btn onClick={savePreviewAndContinue} disabled={!ready} style={{ height: 54, fontSize: 15.5, width: '100%', borderRadius: 16, border: '1px solid rgba(255,255,255,.38)', background: ctaBackground, color: ready ? '#fff' : 'rgba(255,255,255,.72)', boxShadow: ready ? 'inset 0 1px 0 rgba(255,255,255,.56), inset 0 -1px 0 rgba(40,35,170,.18), 0 10px 24px rgba(89,86,246,.30)' : 'inset 0 1px 0 rgba(255,255,255,.45), 0 7px 18px rgba(89,86,246,.12)', textShadow: ready ? '0 1px 1px rgba(35,31,145,.22)' : 'none', transition: 'background .32s ease, color .28s ease, box-shadow .32s ease, transform .18s ease' }}>Start accepting bookings</V2Btn>
            <p style={{ margin: '10px 0 0', textAlign: 'center', fontSize: 12, color: V2C.mute, lineHeight: 1.45 }}>Free to start. Your page and service are already saved.</p>
          </div>
        </V2Step>
      </div>
    </div>
  );
}

export default function PreviewFirstOnboarding() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [previewComplete, setPreviewComplete] = useState(() => searchParams.get('preview') === 'complete' || isPreviewDraft(loadV2Draft()));

  if (loading) return <div aria-label="Loading onboarding" style={{ minHeight: '100dvh', background: SHELL_BACKGROUND, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="animate-spin rounded-full" style={{ width: 48, height: 48, borderBottom: `2px solid ${V2C.brand}` }} /></div>;
  if (user || previewComplete) return <MagicOnboardingPageV2 />;
  return <PreviewBuilder onComplete={() => setPreviewComplete(true)} />;
}
